"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@open-mercato/ui/primitives/button'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { useGuardedMutation } from '@open-mercato/ui/backend/injection/useGuardedMutation'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterBar'

type StationRow = {
  id: string
  code: string
  name: string
  city: string
  country: string
  stationType: string
  latitude: number
  longitude: number
  lineNumbers: string | null
  kmMarker: number | null
  isElectrified: boolean
  isActive: boolean
}

type StationsResponse = {
  items: Array<Record<string, unknown>>
  total: number
  page: number
  totalPages: number
}

function mapStationRow(item: Record<string, unknown>): StationRow | null {
  const id = typeof item.id === 'string' ? item.id : null
  if (!id) return null
  return {
    id,
    code: typeof item.code === 'string' ? item.code : '',
    name: typeof item.name === 'string' ? item.name : '',
    city: typeof item.city === 'string' ? item.city : '',
    country: typeof item.country === 'string' ? item.country : 'POL',
    stationType: typeof item.station_type === 'string' ? item.station_type : '',
    latitude: typeof item.latitude === 'number' ? item.latitude : 0,
    longitude: typeof item.longitude === 'number' ? item.longitude : 0,
    lineNumbers: typeof item.line_numbers === 'string' ? item.line_numbers : null,
    kmMarker: typeof item.km_marker === 'number' ? item.km_marker : null,
    isElectrified: typeof item.is_electrified === 'boolean' ? item.is_electrified : true,
    isActive: typeof item.is_active === 'boolean' ? item.is_active : true,
  }
}

const STATION_TYPE_VALUES = [
  'FREIGHT_TERMINAL',
  'PASSENGER',
  'MIXED',
  'SIDING',
  'PORT_TERMINAL',
  'INLAND_TERMINAL',
  'BORDER_CROSSING',
] as const

export default function RailNetworkStationsPage() {
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const { runMutation } = useGuardedMutation<Record<string, unknown>>({
    contextId: 'rail-network-stations-list',
  })
  const [rows, setRows] = React.useState<StationRow[]>([])
  const [page, setPage] = React.useState(1)
  const pageSize = 50
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [reloadToken, setReloadToken] = React.useState(0)
  const [syncInProgress, setSyncInProgress] = React.useState(false)
  const scopeVersion = useOrganizationScopeVersion()
  const t = useT()

  const getStationTypeLabel = React.useCallback((stationType: string) => {
    const fallbackMap: Record<string, string> = {
      FREIGHT_TERMINAL: 'Freight Terminal',
      PASSENGER: 'Passenger',
      MIXED: 'Mixed',
      SIDING: 'Siding',
      PORT_TERMINAL: 'Port Terminal',
      INLAND_TERMINAL: 'Inland Terminal',
      BORDER_CROSSING: 'Border Crossing',
    }
    return t(`rail_network.stations.types.${stationType}`, fallbackMap[stationType] ?? stationType)
  }, [t])

  const getBooleanLabel = React.useCallback((value: boolean) => {
    return value
      ? t('rail_network.common.yes', 'Yes')
      : t('rail_network.common.no', 'No')
  }, [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'stationType',
      label: t('rail_network.stations.filters.stationType', 'Station Type'),
      type: 'select' as const,
      options: STATION_TYPE_VALUES.map((value) => ({ value, label: getStationTypeLabel(value) })),
    },
    {
      id: 'country',
      label: t('rail_network.stations.filters.country', 'Country'),
      type: 'text' as const,
    },
  ], [getStationTypeLabel, t])

  const queryParams = React.useMemo(() => {
    const params = new URLSearchParams()
    params.set('page', String(page))
    params.set('pageSize', String(pageSize))
    if (search.trim()) params.set('search', search.trim())
    const stationType = filterValues.stationType
    if (typeof stationType === 'string' && stationType.trim()) params.set('stationType', stationType)
    const country = filterValues.country
    if (typeof country === 'string' && country.trim()) params.set('country', country)
    return params.toString()
  }, [filterValues, page, search])

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setIsLoading(true)
      try {
        const fallback: StationsResponse = { items: [], total: 0, page: 1, totalPages: 1 }
        const call = await apiCall<StationsResponse>(
          `/api/rail_network/stations?${queryParams}`,
          undefined,
          { fallback },
        )
        if (!call.ok) {
          const errorPayload = call.result as { error?: string } | undefined
          const message = typeof errorPayload?.error === 'string'
            ? errorPayload.error
            : t('rail_network.stations.error.load', 'Failed to load stations')
          flash(message, 'error')
          return
        }
        const payload = call.result ?? fallback
        if (!cancelled) {
          const items = Array.isArray(payload.items) ? payload.items : []
          setRows(items.map(mapStationRow).filter((row): row is StationRow => row !== null))
          setTotal(typeof payload.total === 'number' ? payload.total : items.length)
          setTotalPages(typeof payload.totalPages === 'number' ? payload.totalPages : 1)
        }
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : t('rail_network.stations.error.load', 'Failed to load stations')
          flash(message, 'error')
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [queryParams, reloadToken, scopeVersion, t])

  const handleRefresh = React.useCallback(() => {
    setReloadToken((token) => token + 1)
  }, [])

  const handleSync = React.useCallback(async () => {
    setSyncInProgress(true)
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          '/api/rail_network/sync',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ provider: 'all' }),
          },
          { errorMessage: t('rail_network.sync.error', 'Synchronization failed') },
        ),
        context: {},
      })
      flash(t('rail_network.sync.success', 'Synchronization completed successfully'), 'success')
      handleRefresh()
    } catch (error) {
      const message = error instanceof Error ? error.message : t('rail_network.sync.error', 'Synchronization failed')
      flash(message, 'error')
    } finally {
      setSyncInProgress(false)
    }
  }, [handleRefresh, runMutation, t])

  const handleDelete = React.useCallback(async (station: StationRow) => {
    if (!station.id) return
    const confirmed = await confirm({
      title: t('rail_network.stations.deleteConfirm', 'Delete station {{name}}?', { name: station.name }),
      variant: 'destructive',
    })
    if (!confirmed) return
    try {
      await runMutation({
        operation: () => apiCallOrThrow(
          `/api/rail_network/stations?id=${encodeURIComponent(station.id)}`,
          { method: 'DELETE', headers: { 'content-type': 'application/json' } },
          { errorMessage: t('rail_network.stations.deleteError', 'Failed to delete station') },
        ),
        context: {},
      })
      setRows((prev) => prev.filter((row) => row.id !== station.id))
      setTotal((prev) => Math.max(prev - 1, 0))
      flash(t('rail_network.stations.deleteSuccess', 'Station deleted'), 'success')
    } catch (error) {
      const message = error instanceof Error ? error.message : t('rail_network.stations.deleteError', 'Failed to delete station')
      flash(message, 'error')
    }
  }, [confirm, runMutation, t])

  const handleFiltersApply = React.useCallback((values: FilterValues) => {
    setFilterValues(values)
    setPage(1)
  }, [])

  const handleFiltersClear = React.useCallback(() => {
    setFilterValues({})
    setPage(1)
  }, [])

  const columns = React.useMemo<ColumnDef<StationRow>[]>(() => {
    const noValue = <span className="text-muted-foreground text-sm">-</span>
    return [
      {
        accessorKey: 'code',
        header: t('rail_network.stations.columns.code', 'Code'),
        cell: ({ row }) => <span className="font-mono font-medium">{row.original.code}</span>,
      },
      {
        accessorKey: 'name',
        header: t('rail_network.stations.columns.name', 'Name'),
        cell: ({ row }) => row.original.name,
      },
      {
        accessorKey: 'city',
        header: t('rail_network.stations.columns.city', 'City'),
      },
      {
        accessorKey: 'country',
        header: t('rail_network.stations.columns.country', 'Country'),
      },
      {
        accessorKey: 'stationType',
        header: t('rail_network.stations.columns.type', 'Type'),
        cell: ({ row }) => getStationTypeLabel(row.original.stationType),
      },
      {
        accessorKey: 'lineNumbers',
        header: t('rail_network.stations.columns.lineNumbers', 'Lines'),
        cell: ({ row }) => row.original.lineNumbers ?? noValue,
      },
      {
        accessorKey: 'kmMarker',
        header: t('rail_network.stations.columns.kmMarker', 'km'),
        cell: ({ row }) => row.original.kmMarker !== null ? `${row.original.kmMarker}` : noValue,
      },
      {
        accessorKey: 'coordinates',
        header: t('rail_network.stations.columns.coordinates', 'Coordinates'),
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {row.original.latitude.toFixed(4)}, {row.original.longitude.toFixed(4)}
          </span>
        ),
      },
      {
        accessorKey: 'isElectrified',
        header: t('rail_network.stations.columns.electrified', 'Electrified'),
        cell: ({ row }) => getBooleanLabel(row.original.isElectrified),
      },
    ]
  }, [getBooleanLabel, getStationTypeLabel, t])

  return (
    <Page>
      <PageBody>
        <div className="mb-4 flex items-center gap-2">
          <Link href="/backend/rail-network/segments">
            <Button variant="outline" size="sm">
              {t('rail_network.nav.segments', 'Segments')}
            </Button>
          </Link>
        </div>
        <DataTable<StationRow>
          title={t('rail_network.stations.title', 'Rail Stations')}
          refreshButton={{
            label: t('rail_network.stations.actions.refresh', 'Refresh'),
            onRefresh: () => { setSearch(''); setPage(1); handleRefresh() },
          }}
          actions={(
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleSync}
                disabled={syncInProgress}
              >
                {syncInProgress
                  ? t('rail_network.sync.inProgress', 'Syncing...')
                  : t('rail_network.sync.button', 'Sync from UTK/OSM')}
              </Button>
              <Button asChild>
                <Link href="/backend/rail-network/stations/create">
                  {t('rail_network.stations.actions.create', 'Add Station')}
                </Link>
              </Button>
            </div>
          )}
          columns={columns}
          data={rows}
          searchValue={search}
          onSearchChange={(value) => { setSearch(value); setPage(1) }}
          searchPlaceholder={t('rail_network.stations.searchPlaceholder', 'Search by name, code, or city...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={handleFiltersApply}
          onFiltersClear={handleFiltersClear}
          perspective={{ tableId: 'rail_network.stations.list' }}
          rowActions={(row) => (
            <RowActions
              items={[
                {
                  id: 'delete',
                  label: t('rail_network.stations.actions.delete', 'Delete'),
                  destructive: true,
                  onSelect: () => { void handleDelete(row) },
                },
              ]}
            />
          )}
          pagination={{ page, pageSize, total, totalPages, onPageChange: setPage }}
          isLoading={isLoading}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
